import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useReserveContext } from '../../../contexts/useReserveContext';
import { fetchStaffOptionsForDate } from '../../../utils/reservation/by-Date/fetchStaffOptionsForDate';
import {
  fetchOpenDatesForStore,
  type OpenDate,
} from '../../../utils/reservation/by-Date/fetchOpenDatesForStore';

// 共通UI
import ReserveStepHeader from '../ReserveStepHeader';
import ReserveCalendarGrid from '../ReserveCalendarGrid';
import { useI18n } from '../../../i18n';

const pad2 = (n: number) => String(n).padStart(2, '0');

const toMonthKeyFromYMD = (ymd?: string | null) => {
  if (!ymd) return null;
  const [y, m] = ymd.split('-');
  if (!y || !m) return null;
  return `${y}-${m}`;
};

const toMonthKeyFromDate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

const SelectDateByDate: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const isEditing = searchParams.get('edit') === '1';
  const {
    storeId,
    skippedStoreSelectionByDate,
    selectedDate,
    selectedStartTime,
    selectedStaffId,
    isFreeReservation,
    selectedServices,
    setSelectedDate,
    setSelectedServices,
    setSelectedStartTime,
    setSelectedStaffId,
    setSelectedStaffName,
    setIsFreeReservation,
  } = useReserveContext();

  const [dates, setDates] = useState<OpenDate[]>([]);
  const [loading, setLoading] = useState(false);
  const serviceIds = useMemo(
    () =>
      selectedServices
        .map((service) => Number(service.serviceId))
        .filter((serviceId) => Number.isFinite(serviceId) && serviceId > 0),
    [selectedServices],
  );

  // 表示月（'YYYY-MM'）
  const [month, setMonth] = useState<string>(() => {
    // 初期：selectedDateがあればその月、なければ今月
    const m = toMonthKeyFromYMD(selectedDate);
    return m ?? toMonthKeyFromDate(new Date());
  });

  // store 未選択ガード
  useEffect(() => {
    if (!storeId) {
      toast.info(t('date.selectStoreFirst'));
      navigate('/reserve/by-date/store');
      return;
    }
    if (
      isEditing &&
      (!selectedStartTime || serviceIds.length === 0 || (!selectedStaffId && !isFreeReservation))
    ) {
      toast.info(t('date.returnAfterSelections'));
      navigate('/reserve/by-date/confirm-reservation');
      return;
    }
  }, [storeId, selectedStartTime, selectedStaffId, isFreeReservation, serviceIds, isEditing, navigate, t]);

  // 候補日取得
  useEffect(() => {
    const load = async () => {
      if (!storeId) return;
      setLoading(true);
      try {
        const data = await fetchOpenDatesForStore(storeId, 90);
        if (isEditing) {
          const filtered = await Promise.all(
            data.map(async (candidate) => {
              if (!candidate.is_open) return null;
              const options = await fetchStaffOptionsForDate({
                storeId,
                date: candidate.date,
                startTime: selectedStartTime!,
                serviceIds,
              });
              const allowed = isFreeReservation
                ? options.free_available
                : options.staffs.some((staff) => staff.staff_id === selectedStaffId);
              return allowed ? candidate : null;
            }),
          );
          setDates(filtered.filter((value): value is OpenDate => Boolean(value)));
        } else {
          setDates(data);
        }

        // すでに選択済みの日付がある場合、その月にカレンダーを合わせる
        const m = toMonthKeyFromYMD(selectedDate);
        if (m) setMonth(m);
      } catch (err) {
        console.error(err);
        toast.error(t('date.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [storeId, selectedDate, selectedStartTime, selectedStaffId, isFreeReservation, serviceIds, isEditing, t]);

  const openDateSet = useMemo(() => {
    const set = new Set<string>();
    for (const d of dates) {
      if (d.is_open) set.add(d.date);
    }
    return set;
  }, [dates]);

  // 月移動の制限（90日分の範囲内で移動）
  const { minMonth, maxMonth } = useMemo(() => {
    const open = dates.filter((d) => d.is_open).map((d) => d.date).sort();
    if (open.length === 0) return { minMonth: undefined as string | undefined, maxMonth: undefined as string | undefined };
    const first = open[0].slice(0, 7);
    const last = open[open.length - 1].slice(0, 7);
    return { minMonth: first, maxMonth: last };
  }, [dates]);

  const onSelect = (ymd: string) => {
    if (!openDateSet.has(ymd)) {
      toast.info(t('date.unavailable'));
      return;
    }

    setSelectedDate(ymd);
    if (isEditing) {
      navigate('/reserve/by-date/confirm-reservation');
      return;
    }

    setSelectedStartTime(null);
    setSelectedServices([]);
    setSelectedStaffId(null);
    setSelectedStaffName(null);
    setIsFreeReservation(false);
    navigate('/reserve/by-date/time');
  };

  return (
    <div className="flex flex-col min-h-[70vh] space-y-4">
      {/* 共通ヘッダー */}
      <ReserveStepHeader
        title={t('date.chooseTitle')}
        onBack={() =>
          navigate(
            isEditing
              ? '/reserve/by-date/confirm-reservation'
              : skippedStoreSelectionByDate
                ? '/reserve'
                : '/reserve/by-date/store',
          )
        }
        step={2}
        totalSteps={5}
      />

      {loading ? (
        <p className="text-center text-slate-400 text-sm">{t('date.loadingCalendar')}</p>
      ) : openDateSet.size === 0 ? (
        <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-rose-100 p-2 text-rose-600">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-rose-700">{t('date.cannotChange')}</div>
              <div className="mt-1 text-sm leading-6 text-rose-600">
                {t('date.noAlternative')}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <ReserveCalendarGrid
          month={month}
          onMonthChange={setMonth}
          selectedDate={selectedDate}
          openDateSet={openDateSet}
          onSelectDate={onSelect}
          minMonth={minMonth}
          maxMonth={maxMonth}
          weekStartsOn={1}
        />
      )}
    </div>
  );
};

export default SelectDateByDate;
